import { useEffect, useState } from "react";
import { Alert, message, Tooltip } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import VLButton from "views/uikit/Buttons/VLButton";
import Loader from "views/uikit/Loader";
import ContainerHeaderLayout from "views/components/ContainerHeaderLayout/ContainerHeaderLayout";
import useCreateDataset from "hooks/useCreateDataset";
import useScanResultsAvailability from "hooks/useScanResultsAvailability";
import { resetStore } from "redux/CreateDataset/actions";
import { useDispatch } from "react-redux";

import ScanResultsBrowser, {
  describeSelection,
  SelectedWafers,
} from "./ScanResultsBrowser";
import useScanResultsExport from "./useScanResultsExport";
import useTargetDataset from "./useTargetDataset";
import { postRequest } from "helpers/utility/axiosServiceLayer";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import styles from "./style.module.scss";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

/**
 * Pick scan results from DataServer, export them to ADC, and continue with the folders.
 *
 * Two modes, chosen by the route. At /dataset/scan-results the exports become a new
 * dataset. At /dataset/:datasetId/scan-results they are added to that dataset as a media
 * addition: the search is pinned to the dataset's Job and Setup, and scan results the
 * dataset would refuse cannot be selected.
 *
 * The creation wizard imports through ScanResultsModal instead; this page stays for
 * direct links into either mode.
 */
export default function ScanResultsPage() {
  const { datasetId } = useParams();
  const { target, error: targetError } = useTargetDataset(datasetId);
  const [selectedWafers, setSelectedWafers] = useState<SelectedWafers>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createNewDataset } = useCreateDataset();
  const { exportSelection, exportProgress } = useScanResultsExport();
  const { availability } = useScanResultsAvailability();

  // Reachable by bookmark and by refresh, so the availability gate has to hold here.
  // Checked once at mount rather than on every fetch: a 502 mid-session belongs in the
  // browser's alert, where it says what went wrong, instead of bouncing someone out of
  // the page they were working in.
  useEffect(() => {
    if (!availability || availability.available) return;
    message.info(
      availability.configured
        ? "Scan results are unavailable: DataServer could not be reached."
        : "Scan results are unavailable: DataServer is not configured for this deployment.",
    );
    navigate(
      datasetId ? `/dataset/${datasetId}/update` : PRIVATE_DATASETS_PATH,
      { replace: true },
    );
  }, [availability, navigate, datasetId]);

  const selectedCount = Object.keys(selectedWafers).length;

  /**
   * Export the selection to ADC, then open the new dataset on Add Data with the exported
   * folders already in it.
   *
   * Export first, create second. The other order leaves an orphan dataset row behind when
   * every export fails.
   */
  const handleContinue = async () => {
    if (!selectedCount || isSubmitting || exportProgress) return;

    const { exportedFolders } = await exportSelection(selectedWafers);
    // Nothing to put in a dataset, so do not create one.
    if (!exportedFolders.length) return;

    setIsSubmitting(true);
    if (datasetId) {
      try {
        // A media addition runs in a batch. This returns the dataset's open batch if it
        // has one, else a new one. Opening the update page with the batch already in the
        // URL matters: without it the page creates one and redirects, and that redirect
        // drops the router state carrying the folders.
        const { batch_id: batchId } = await postRequest<
          { batch_id: string },
          undefined
        >(
          composeApiURL(HOST_URL, `/workflow/dataset/${datasetId}/batch/new`),
          undefined,
        );
        dispatch(resetStore());
        navigate(`/dataset/${datasetId}/update/${batchId}/on-prem`, {
          state: { exportedFolders },
        });
      } catch (e: any) {
        message.error(
          `Exported, but could not start adding media: ${
            e.response?.data?.detail || "request failed"
          }`,
          8,
        );
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    try {
      // Same opening move as the folder flow: clear any previous wizard state, then
      // create the dataset the exported folders will belong to.
      dispatch(resetStore());
      const newDatasetId = await createNewDataset();
      if (!newDatasetId) return;
      navigate(`/dataset/${newDatasetId}/create`, {
        // Read by AddDataContent, which replays them through the same handler the path
        // form uses. Datasets-relative folder names, never the host paths above.
        state: { exportedFolders },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const continueDisabledReason = !selectedCount
    ? "Select at least one scan result"
    : "";

  return (
    <>
      <Helmet>
        <title>Scan Results</title>
      </Helmet>
      <ContainerHeaderLayout
        upperRow={
          <div className={styles.pageHeader}>
            {target
              ? `Scan Results - adding to ${target.name}`
              : "Scan Results"}
          </div>
        }
      />
      <div className={styles.content}>
        <div className={styles.scanResultsSection}>
          {!availability || !availability.available ? (
            // Either still asking, or about to redirect. Showing the grid in between
            // would flash an empty table on a deployment that has no DataServer.
            <div className={styles.loader}>
              <Loader />
            </div>
          ) : (
            <>
              {targetError && (
                <Alert
                  type="error"
                  showIcon
                  message={targetError}
                  className={styles.alert}
                />
              )}
              <ScanResultsBrowser
                selectedWafers={selectedWafers}
                onSelectedWafersChange={setSelectedWafers}
                target={target}
                targetPending={!!datasetId && !target}
              />
              <div className={styles.footer}>
                <span className={styles.selectionCount}>
                  {describeSelection(selectedWafers)}
                </span>
                <Tooltip title={continueDisabledReason}>
                  {/* The only action here: Continue exports to ADC and then opens the
                      new dataset on Add Data. A separate export button would run the
                      same multi-minute job and leave the user with nowhere to go. */}
                  <VLButton
                    type="primary"
                    size="middle"
                    onClick={handleContinue}
                    disabled={
                      !!continueDisabledReason ||
                      !!exportProgress ||
                      isSubmitting
                    }
                    loading={!!exportProgress || isSubmitting}
                  >
                    {exportProgress || "Continue"}
                  </VLButton>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
